'use client'

import { useState, useCallback } from 'react'
import ToastComponent, { Toast } from './Toast'

interface ToastContainerProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
}

const positionClasses = {
  'top-right': 'top-4 right-4',
  'top-left': 'top-4 left-4',
  'bottom-right': 'bottom-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'top-center': 'top-4 left-1/2 -translate-x-1/2',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
}

let toastContainer: ToastContainerRef | null = null

interface ToastContainerRef {
  addToast: (toast: Omit<Toast, 'id'>) => void
}

export function ToastContainer({ position = 'top-right' }: ToastContainerProps) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback((toastData: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9)
    const toast: Toast = { ...toastData, id }
    
    setToasts(prev => {
      // Limit to 5 toasts maximum
      const newToasts = [toast, ...prev].slice(0, 5)
      return newToasts
    })
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }, [])

  // Store reference for global access
  if (toastContainer) {
    toastContainer.addToast = addToast
  } else {
    toastContainer = { addToast }
  }

  return (
    <div className={`fixed z-[100] pointer-events-none ${positionClasses[position]}`}>
      <div className="flex flex-col space-y-3 max-w-sm">
        {toasts.map((toast) => (
          <ToastComponent
            key={toast.id}
            {...toast}
            onRemove={removeToast}
          />
        ))}
      </div>
    </div>
  )
}

// Global toast functions
export const toast = {
  success: (title: string, description?: string, duration?: number) => {
    toastContainer?.addToast({ type: 'success', title, description, duration })
  },
  error: (title: string, description?: string, duration?: number) => {
    toastContainer?.addToast({ type: 'error', title, description, duration })
  },
  info: (title: string, description?: string, duration?: number) => {
    toastContainer?.addToast({ type: 'info', title, description, duration })
  },
  warning: (title: string, description?: string, duration?: number) => {
    toastContainer?.addToast({ type: 'warning', title, description, duration })
  },
}

export default ToastContainer